// src/components/public_utilities/MapFilterBar.js
// --- UPDATE (Public Utilities v3)
//   All new props are optional — without them the bar renders exactly as before.
//     layerOptions        [{ key, label, colorScheme }] replaces the built-in layer list (a page shows only
//                         the layers it actually has; the Public Utilities map has no roads)
//     puColorBy / onPuColorBy        "Colour by" menu (type · status · next inspection · managed by)
//     puTypes             [{ key, label, color, count }]  type filter with real counts
//     puVisibleTypes / onTogglePuType / onShowAllPuTypes
//     showPuZones / onTogglePuZones  constituency outlines
//     showInspector       false hides the 4D inspector button (default true)
//   Hooks are at the top level; menus are keyboard accessible (Chakra Menu).
// --- END UPDATE
import React, { memo } from 'react';
import {
  HStack,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Tooltip,
  Badge,
  useColorModeValue,
  Flex
} from '@chakra-ui/react';
import {
  MdFilterList,
  MdFullscreen,
  MdFullscreenExit,
  MdShield,
  MdPalette, MdCategory, MdLayers // --- UPDATE (v3)
} from 'react-icons/md';
// --- UPDATE (v3)
import { PU_COLOR_BY } from './pu/puMapStyles';
const DEFAULT_LAYERS = [
  { key: 'pipelines', label: 'Pipelines', colorScheme: 'purple' },
  { key: 'roads', label: 'Roads / Streets', colorScheme: 'blue' },
  { key: 'pressureSensors', label: 'Pressure Sensors', colorScheme: 'teal' },
  { key: 'waterLevelSensors', label: 'Water Level Sensors', colorScheme: 'cyan' },
  { key: 'assets', label: 'Facility Assets', colorScheme: 'orange' },
  { key: 'zones', label: 'Field Zones', colorScheme: 'yellow' },
  { key: 'anomalies', label: 'AI Anomalies', colorScheme: 'red' },
];
// --- END UPDATE

const MapFilterBar = ({
  visibleLayers,
  onToggleLayer,
  isFullScreen,
  onToggleFullScreen,
  isInspectingClearance,
  onToggleClearanceInspector,
  // --- UPDATE (v3)
  layerOptions,
  puColorBy,
  onPuColorBy,
  puTypes,
  puVisibleTypes,
  onTogglePuType,
  onShowAllPuTypes,
  showPuZones,
  onTogglePuZones,
  showInspector = true,
  // --- END UPDATE
}) => {
  const barBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  // --- UPDATE (v3)
  const countColor = useColorModeValue('gray.500', 'gray.400');
  const layers = Array.isArray(layerOptions) && layerOptions.length ? layerOptions : DEFAULT_LAYERS;
  const typeSet = puVisibleTypes instanceof Set ? puVisibleTypes : null;
  const allTypesOn = !typeSet || typeSet.size === 0;
  // --- END UPDATE

  return (
    <Flex
      position="absolute"
      top="12px"
      right="12px"
      zIndex={1000}
      bg={barBg}
      backdropFilter="blur(8px)"
      p={1.5}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="md"
      gap={2}
      align="center"
    >
      {/* --- UPDATE (v3): Public Utilities colour + type filter */}
      {onPuColorBy && (
        <Menu>
          <Tooltip label="Colour assets by" hasArrow>
            <MenuButton as={Button} size="sm" leftIcon={<MdPalette />} variant="ghost" fontSize="xs" borderRadius="lg" aria-label="Colour assets by">
              {(PU_COLOR_BY.find(o => o.value === puColorBy) || PU_COLOR_BY[0]).label}
            </MenuButton>
          </Tooltip>
          <MenuList minW="180px" p={2} zIndex={1500} fontSize="sm">
            {PU_COLOR_BY.map(o => (
              <MenuItem key={o.value} onClick={() => onPuColorBy(o.value)} fontWeight={o.value === puColorBy ? 700 : 400}>
                {o.value === puColorBy ? '● ' : ''}{o.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
      {onTogglePuType && Array.isArray(puTypes) && puTypes.length > 0 && (
        <Menu closeOnSelect={false}>
          <Tooltip label="Show asset types" hasArrow>
            <MenuButton as={Button} size="sm" leftIcon={<MdCategory />} variant="ghost" fontSize="xs" borderRadius="lg" aria-label="Show asset types">
              Types{allTypesOn ? '' : ` (${typeSet.size})`}
            </MenuButton>
          </Tooltip>
          <MenuList minW="220px" maxH="320px" overflowY="auto" p={2} zIndex={1500} fontSize="sm">
            {onShowAllPuTypes && !allTypesOn && <MenuItem onClick={onShowAllPuTypes} fontWeight={700}>Show all types</MenuItem>}
            {puTypes.map(t => (
              <MenuItem key={t.key} onClick={() => onTogglePuType(t.key)}>
                <Checkbox isChecked={allTypesOn || typeSet.has(t.key)} pointerEvents="none" mr={2} colorScheme="teal" />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, display: 'inline-block', marginRight: 8 }} />
                <span style={{ flex: 1 }}>{t.label}</span>
                <Badge ml={2} variant="subtle" color={countColor}>{t.count}</Badge>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
      {onTogglePuZones && (
        <Tooltip label={showPuZones ? 'Hide constituency outlines' : 'Show constituency outlines'} hasArrow>
          <IconButton icon={<MdLayers size={18} />} size="sm" variant={showPuZones ? 'solid' : 'ghost'} colorScheme="purple" borderRadius="lg"
            onClick={onTogglePuZones} aria-label="Toggle constituency outlines" aria-pressed={!!showPuZones} />
        </Tooltip>
      )}
      {/* --- END UPDATE */}

      {/* 4D Subsurface Inspector Tool Toggle */}
      {showInspector && ( /* --- UPDATE (v3): optional */
      <Tooltip label={isInspectingClearance ? "Disarm Clearance Inspector" : "Arm 4D Subsurface Inspector"} hasArrow>
        <Button
          size="sm"
          leftIcon={<MdShield />}
          colorScheme={isInspectingClearance ? "red" : "purple"}
          variant={isInspectingClearance ? "solid" : "outline"}
          onClick={onToggleClearanceInspector}
          fontSize="xs"
          borderRadius="lg"
        >
          {isInspectingClearance ? "Inspecting" : "Inspect 4D"}
        </Button>
      </Tooltip>
      )}

      {/* Layer Visibility Menu */}
      <Menu closeOnSelect={false}>
        <Tooltip label="Filter Map Layers" hasArrow>
          <MenuButton
            as={Button}
            size="sm"
            leftIcon={<MdFilterList />}
            variant="ghost"
            fontSize="xs"
            borderRadius="lg"
          >
            Layers
          </MenuButton>
        </Tooltip>
        <MenuList minW="180px" p={2} zIndex={1500} fontSize="sm">
          {/* --- UPDATE (v3): same items, data-driven (layerOptions can replace them) */}
          {layers.map(l => (
            <MenuItem key={l.key} onClick={() => onToggleLayer(l.key)}>
              <Checkbox isChecked={!!visibleLayers?.[l.key]} pointerEvents="none" mr={2} colorScheme={l.colorScheme || 'teal'} />
              {l.label}
            </MenuItem>
          ))}
          {/* --- END UPDATE */}
        </MenuList>
      </Menu>

      {/* Fullscreen Enlarge Toggle */}
      <Tooltip label={isFullScreen ? "Exit Fullscreen" : "Enlarge Map (Fullscreen)"} hasArrow>
        <IconButton
          icon={isFullScreen ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
          size="sm"
          colorScheme="blue"
          variant="ghost"
          borderRadius="lg"
          onClick={onToggleFullScreen}
          aria-label="Toggle Fullscreen"
        />
      </Tooltip>
    </Flex>
  );
};

export default memo(MapFilterBar);